import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AppIconButton from '../components/ui/AppIconButton';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  TextField,
  IconButton,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Divider,
  Card,
  CardContent,
  CardActions,
} from '@mui/material';
import {
  Upload as UploadIcon,
  Check as CheckIcon,
  ExpandMore as ExpandMoreIcon,
  Delete as DeleteIcon,
  Restaurant as RestaurantIcon,
  Category as CategoryIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import api from '../api/api';

interface Vendor {
  id: number;
  username: string;
  businessName?: string;
  email?: string;
}

interface ExtractedItem {
  name: string;
  description: string;
  price: number;
  tags?: string[];
  portions?: Array<{ name: string; price: number }>;
}

interface ExtractedCategory {
  name: string;
  description: string;
  items: ExtractedItem[];
}

interface ExtractionResult {
  success: boolean;
  message: string;
  extractedCategories: number;
  extractedItems: number;
  menuDataPreview?: {
    menuName: string;
    categories: ExtractedCategory[];
    extractedJson: string;
  };
  errorDetails?: string;
}

interface ExistingCategory {
  id: number;
  name: string;
  description?: string;
  itemCount: number;
}

const AdminMenuManagement: React.FC = () => {
  const navigate = useNavigate();
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [selectedVendor, setSelectedVendor] = useState<number | ''>('');
  const [loadingVendors, setLoadingVendors] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Existing menu state
  const [existingCategories, setExistingCategories] = useState<ExistingCategory[]>([]);
  const [loadingExisting, setLoadingExisting] = useState(false);

  // Extraction state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [extracting, setExtracting] = useState(false);
  const [extractionResult, setExtractionResult] = useState<ExtractionResult | null>(null);
  const [editingCategories, setEditingCategories] = useState<ExtractedCategory[]>([]);

  // Saving state
  const [saving, setSaving] = useState(false);
  const [savedCategories, setSavedCategories] = useState<string[]>([]);

  // Load vendors on mount
  useEffect(() => {
    fetchVendors();
  }, []);

  // Load existing categories when vendor is selected
  useEffect(() => {
    if (selectedVendor) {
      fetchExistingCategories(selectedVendor as number);
    } else {
      setExistingCategories([]);
    }
  }, [selectedVendor]);

  const fetchVendors = async () => {
    try {
      setLoadingVendors(true);
      const response = await api.get('/api/admin/vendors');
      setVendors(response.data || []);
    } catch (err) {
      console.error('Failed to fetch vendors:', err);
      setError('Failed to load vendors');
    } finally {
      setLoadingVendors(false);
    }
  };

  const fetchExistingCategories = async (vendorId: number) => {
    try {
      setLoadingExisting(true);
      const response = await api.get(`/api/admin/vendors/${vendorId}/categories`);
      setExistingCategories(response.data || []);
    } catch (err) {
      console.error('Failed to fetch existing categories:', err);
      // Don't show error - vendor might not have any menu yet
      setExistingCategories([]);
    } finally {
      setLoadingExisting(false);
    }
  };

  const handleManageFullMenu = () => {
    if (selectedVendor) {
      navigate(`/admin/vendors/${selectedVendor}/menu`);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file
    if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
      setError('Please select an image or PDF file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB');
      return;
    }

    setSelectedFile(file);
    setExtractionResult(null);
    setEditingCategories([]);
    setError(null);
    setSavedCategories([]);
  };

  const handleExtract = async () => {
    if (!selectedFile) return;

    setExtracting(true);
    setError(null);
    setExtractionResult(null);

    try {
      const formData = new FormData();
      formData.append('files', selectedFile);

      const response = await api.post('/api/menu-extraction/extract-multiple', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const result: ExtractionResult = response.data;
      setExtractionResult(result);

      if (result.success && result.menuDataPreview?.categories) {
        setEditingCategories(result.menuDataPreview.categories);
      }
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { message?: string; errorDetails?: string } } };
      setError(axiosError.response?.data?.message || 'Failed to extract menu from file');
    } finally {
      setExtracting(false);
    }
  };

  const handleSaveCategory = async (category: ExtractedCategory) => {
    if (!selectedVendor) {
      setError('Please select a vendor first');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // First, create the category
      const categoryResponse = await api.post(`/api/admin/vendors/${selectedVendor}/categories`, {
        name: category.name,
        description: category.description,
      });

      const categoryId = categoryResponse.data.id;

      // Then, create all items in that category as a batch
      const itemsToCreate = category.items.map(item => ({
        name: item.name,
        description: item.description,
        price: item.price,
        categoryId: categoryId,
      }));

      await api.post(`/api/admin/vendors/${selectedVendor}/menu-items`, itemsToCreate);

      setSavedCategories(prev => [...prev, category.name]);
      setSuccess(`Category "${category.name}" with ${category.items.length} items saved successfully!`);

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { message?: string } } };
      setError(axiosError.response?.data?.message || `Failed to save category "${category.name}"`);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAllCategories = async () => {
    if (!selectedVendor) {
      setError('Please select a vendor first');
      return;
    }

    setSaving(true);
    setError(null);

    let successCount = 0;
    const errors: string[] = [];

    for (let i = 0; i < editingCategories.length; i++) {
      const category = editingCategories[i];
      if (savedCategories.includes(category.name)) continue; // Skip already saved

      try {
        const categoryResponse = await api.post(`/api/admin/vendors/${selectedVendor}/categories`, {
          name: category.name,
          description: category.description,
        });

        const categoryId = categoryResponse.data.id;

        // Create all items in batch
        const itemsToCreate = category.items.map(item => ({
          name: item.name,
          description: item.description,
          price: item.price,
          categoryId: categoryId,
        }));

        await api.post(`/api/admin/vendors/${selectedVendor}/menu-items`, itemsToCreate);

        setSavedCategories(prev => [...prev, category.name]);
        successCount++;
      } catch {
        errors.push(category.name);
      }
    }

    setSaving(false);

    if (successCount > 0) {
      setSuccess(`Successfully saved ${successCount} categories!`);
    }
    if (errors.length > 0) {
      setError(`Failed to save: ${errors.join(', ')}`);
    }
  };

  const handleEditItem = (catIndex: number, itemIndex: number, field: keyof ExtractedItem, value: string | number) => {
    setEditingCategories(prev => {
      const updated = [...prev];
      const item = { ...updated[catIndex].items[itemIndex] };
      if (field === 'name' || field === 'description') {
        item[field] = value as string;
      } else if (field === 'price') {
        item[field] = value as number;
      }
      updated[catIndex].items[itemIndex] = item;
      return updated;
    });
  };

  const handleDeleteItem = (catIndex: number, itemIndex: number) => {
    setEditingCategories(prev => {
      const updated = [...prev];
      updated[catIndex].items.splice(itemIndex, 1);
      return updated;
    });
  };

  const handleDeleteCategory = (catIndex: number) => {
    setEditingCategories(prev => {
      const updated = [...prev];
      updated.splice(catIndex, 1);
      return updated;
    });
  };

  const handleReset = () => {
    setSelectedFile(null);
    setExtractionResult(null);
    setEditingCategories([]);
    setSavedCategories([]);
    setError(null);
    setSuccess(null);
  };

  const selectedVendorData = vendors.find(v => v.id === selectedVendor);

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>
        Menu Management
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Import Menu from Image
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Upload a menu image to extract categories and items. You can edit the extracted data before saving to a vendor.
        </Typography>

        <Grid container spacing={3}>
          {/* Step 1: Select Vendor */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Select Vendor</InputLabel>
              <Select
                value={selectedVendor}
                onChange={(e) => setSelectedVendor(e.target.value as number)}
                label="Select Vendor"
                disabled={loadingVendors}
              >
                {vendors.map((vendor) => (
                  <MenuItem key={vendor.id} value={vendor.id}>
                    {vendor.businessName || vendor.username} ({vendor.username})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Step 2: Upload File */}
          <Grid item xs={12} md={6}>
            <Button
              variant="outlined"
              component="label"
              fullWidth
              startIcon={<UploadIcon />}
              disabled={!selectedVendor || extracting}
              sx={{ height: 56 }}
            >
              {selectedFile ? selectedFile.name : 'Select Menu Image (one at a time)'}
              <input
                type="file"
                hidden
                accept="image/*,.pdf"
                onChange={handleFileSelect}
              />
            </Button>
          </Grid>

          {/* Extract Button */}
          {selectedFile && (
            <Grid item xs={12}>
              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  onClick={handleExtract}
                  disabled={extracting || !selectedVendor}
                  startIcon={extracting ? <CircularProgress size={20} /> : <RestaurantIcon />}
                >
                  {extracting ? 'Extracting...' : 'Extract Menu'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleReset}
                  startIcon={<RefreshIcon />}
                >
                  Reset
                </Button>
              </Stack>
            </Grid>
          )}
        </Grid>

        {/* Extraction Progress */}
        {extracting && (
          <Box sx={{ mt: 3 }}>
            <LinearProgress />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Extracting menu data from image... This may take a moment.
            </Typography>
          </Box>
        )}

        {/* Error/Success Messages */}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mt: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}
      </Paper>

      {/* Extraction Results */}
      {extractionResult && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Extracted Menu Data
            </Typography>
            {extractionResult.success && (
              <Chip
                icon={<CheckIcon />}
                label={`${extractionResult.extractedCategories} categories, ${extractionResult.extractedItems} items`}
                color="success"
              />
            )}
          </Box>

          {extractionResult.success ? (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                Review and edit the extracted data below. Click "Save Category" to add each category to{' '}
                <strong>{selectedVendorData?.businessName || selectedVendorData?.username}</strong>'s menu.
              </Alert>

              {/* Save All Button */}
              <Box sx={{ mb: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSaveAllCategories}
                  disabled={saving || editingCategories.length === 0 || savedCategories.length === editingCategories.length}
                  startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                >
                  Save All Categories ({editingCategories.length - savedCategories.length} remaining)
                </Button>
              </Box>

              {/* Categories */}
              {editingCategories.map((category, catIndex) => {
                const isSaved = savedCategories.includes(category.name);
                return (
                  <Accordion
                    key={catIndex}
                    defaultExpanded={catIndex === 0}
                    sx={{
                      mb: 1,
                      border: isSaved ? '2px solid #10b981' : '1px solid #e2e8f0',
                      '&:before': { display: 'none' },
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                        <CategoryIcon color={isSaved ? 'success' : 'primary'} />
                        <Typography sx={{ fontWeight: 600, flex: 1 }}>
                          {category.name}
                        </Typography>
                        <Chip
                          label={`${category.items.length} items`}
                          size="small"
                          color={isSaved ? 'success' : 'default'}
                        />
                        {isSaved && (
                          <Chip label="Saved" size="small" color="success" icon={<CheckIcon />} />
                        )}
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      {category.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {category.description}
                        </Typography>
                      )}

                      {/* Items in category */}
                      <List dense>
                        {category.items.map((item, itemIndex) => (
                          <ListItem
                            key={itemIndex}
                            sx={{
                              bgcolor: 'var(--bg-sunken)',
                              borderRadius: 1,
                              mb: 1,
                              border: '1px solid var(--border-subtle)',
                            }}
                          >
                            <ListItemIcon>
                              <RestaurantIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <TextField
                                  value={item.name}
                                  onChange={(e) => handleEditItem(catIndex, itemIndex, 'name', e.target.value)}
                                  size="small"
                                  variant="standard"
                                  disabled={isSaved}
                                  sx={{ fontWeight: 500 }}
                                />
                              }
                              secondary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5 }}>
                                  <TextField
                                    value={item.description}
                                    onChange={(e) => handleEditItem(catIndex, itemIndex, 'description', e.target.value)}
                                    size="small"
                                    variant="standard"
                                    disabled={isSaved}
                                    placeholder="Description"
                                    sx={{ flex: 1 }}
                                  />
                                  <TextField
                                    value={item.price}
                                    onChange={(e) => handleEditItem(catIndex, itemIndex, 'price', parseFloat(e.target.value) || 0)}
                                    size="small"
                                    variant="standard"
                                    type="number"
                                    disabled={isSaved}
                                    sx={{ width: 80 }}
                                    InputProps={{ startAdornment: '₹' }}
                                  />
                                </Box>
                              }
                            />
                            {!isSaved && (
                              <AppIconButton
                                icon={<DeleteIcon fontSize="small" />}
                                tooltip="Delete"
                                variant="danger"
                                size="small"
                                onClick={() => handleDeleteItem(catIndex, itemIndex)}
                                data-testid="delete-menu-item"
                              />
                            )}
                          </ListItem>
                        ))}
                      </List>

                      {/* Save Category Button */}
                      <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                        {!isSaved ? (
                          <>
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => handleSaveCategory(category)}
                              disabled={saving}
                              startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                            >
                              Save Category
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              color="error"
                              onClick={() => handleDeleteCategory(catIndex)}
                              startIcon={<DeleteIcon />}
                            >
                              Remove
                            </Button>
                          </>
                        ) : (
                          <Alert severity="success" sx={{ py: 0 }}>
                            Category saved to vendor's menu
                          </Alert>
                        )}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </>
          ) : (
            <Alert severity="error">
              {extractionResult.message}
              {extractionResult.errorDetails && (
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  {extractionResult.errorDetails}
                </Typography>
              )}
            </Alert>
          )}
        </Paper>
      )}

      {/* Existing Menu Section */}
      {selectedVendor && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Existing Menu for {selectedVendorData?.businessName || selectedVendorData?.username}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<EditIcon />}
              onClick={handleManageFullMenu}
            >
              Manage Full Menu
            </Button>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {loadingExisting ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : existingCategories.length === 0 ? (
            <Alert severity="info">
              No menu categories found for this vendor. Upload a menu image above to create categories.
            </Alert>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                This vendor has {existingCategories.length} existing categories. Click "Manage Full Menu" to edit items, or upload more images to add new categories.
              </Typography>
              <Grid container spacing={2}>
                {existingCategories.map((category) => (
                  <Grid item xs={12} sm={6} md={4} key={category.id}>
                    <Card variant="outlined">
                      <CardContent sx={{ pb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CategoryIcon color="primary" />
                          <Typography variant="subtitle1" fontWeight={600}>
                            {category.name}
                          </Typography>
                        </Box>
                        {category.description && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            {category.description}
                          </Typography>
                        )}
                        <Chip
                          label={`${category.itemCount} items`}
                          size="small"
                          sx={{ mt: 1 }}
                          icon={<RestaurantIcon />}
                        />
                      </CardContent>
                      <CardActions>
                        <Button
                          size="small"
                          startIcon={<ViewIcon />}
                          onClick={handleManageFullMenu}
                        >
                          View Items
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </>
          )}
        </Paper>
      )}

      {/* Instructions */}
      {!extractionResult && !selectedVendor && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            How it works
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon><Chip label="1" size="small" /></ListItemIcon>
              <ListItemText
                primary="Select a vendor"
                secondary="Choose the vendor whose menu you want to manage"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><Chip label="2" size="small" /></ListItemIcon>
              <ListItemText
                primary="Upload ONE menu image"
                secondary="Upload one image at a time for best results (JPG, PNG, or PDF under 10MB)"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><Chip label="3" size="small" /></ListItemIcon>
              <ListItemText
                primary="Review extracted data"
                secondary="Edit category names, item names, descriptions, and prices as needed"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><Chip label="4" size="small" /></ListItemIcon>
              <ListItemText
                primary="Save categories"
                secondary="Save each category individually or all at once to the vendor's menu"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><Chip label="5" size="small" /></ListItemIcon>
              <ListItemText
                primary="Repeat for more images"
                secondary="Upload additional menu images to add more categories"
              />
            </ListItem>
          </List>
        </Paper>
      )}
    </Box>
  );
};

export default AdminMenuManagement;
